import {
  ChangeEvent,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { html } from 'lit/static-html.js';
import { ColumnDef } from '@tanstack/react-table';
import { langs } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { proStudioUrqlGql } from '@pypestream/api-services/urql';

import {
  ActionFile,
  ActionSection,
  useConfirmDeleteModal,
  useEditorModal,
  useFileConflictModal,
} from '../components';
import { RowSelectionChangeProps } from '../components/action-section/action-section';
import { useAgentManagerFiles, useProStudioViewerRole } from '../hooks';
import {
  ACTION_FILE_ACCEPT,
  AgentManagerConflictError,
  buildUploadChanges,
  CONFIG_FILE_ACCEPT,
  downloadOneAgentManagerFile,
  fetchAgentManagerFileText,
  findUploadConflicts,
  isEditableActionFile,
  readTypeRelativePath,
  syncAgentManagerFiles,
} from '../utils';
import { isRepositoryScaffoldingFile } from '../utils/repository-scaffolding';
import { sendUserMessage } from '../xstate/app.xstate-utils';
import { useBobcatFlag } from '../hooks/use-bobcat-flag';
import { resolveEditorContent } from './action-editor-content';
import { useSecretHiddenNotice } from './use-secret-hidden-notice';

type SectionKind = 'config' | 'scripts';

type ActionsContentProps = {
  /** A line under a script's name — where the panel says which nodes use it. */
  scriptNote?: (fileName: string) => string | undefined;
  /** Drag a script's row onto the canvas or table as an action node. */
  onScriptDragStart?: (fileName: string, e: DragEvent) => void;
  onScriptDragEnd?: () => void;
  betweenSections?: ReactNode;
  /** Build's Actions panel search: only files whose name contains it. */
  query?: string;
  /**
   * The scripts' file input, for the panel's own "New action" button: the
   * Custom section has no upload button of its own.
   */
  scriptsUploadRef?: RefObject<HTMLInputElement>;
};

const reportError = (error: unknown, fallback: string) =>
  sendUserMessage({
    type: 'error',
    text: error instanceof Error ? error.message : fallback,
    autoClose: 5000,
  });

const toActionFiles = (
  files: { path?: string | null; name?: string | null }[]
): ActionFile[] =>
  files
    .filter((file) => file.path)
    .map((file) => ({
      name: file.name || file.path?.split('/').pop() || '',
      path: file.path as string,
    }));

/**
 * The solution's scripts and config files for Build's Actions panel (Project
 * Bobcat): the scripts first, as "Custom", the panel's word for the solution's
 * own commands, then `betweenSections`, then the config files. Lists, uploads,
 * edits and deletes go through the Agent Manager, as the Files page's Actions
 * tab does (`files/actions-tab.tsx`); only the layout is the panel's.
 */
export const ActionsContent: FC<ActionsContentProps> = ({
  scriptNote,
  onScriptDragStart,
  onScriptDragEnd,
  betweenSections,
  query,
  scriptsUploadRef,
}) => {
  const [t] = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const isViewer = useProStudioViewerRole();

  const config = useAgentManagerFiles('config');
  const scripts = useAgentManagerFiles('scripts');
  const sectionOf = useCallback(
    (kind: SectionKind) => (kind === 'config' ? config : scripts),
    [config, scripts]
  );

  const { confirm: confirmOverwrite, fileConflictModal } =
    useFileConflictModal();
  const { confirm: confirmDelete, confirmDeleteModal } =
    useConfirmDeleteModal();

  const ownScriptsInput = useRef<HTMLInputElement>(null);
  const scriptsInput = scriptsUploadRef ?? ownScriptsInput;

  const [editor, setEditor] = useState<
    { path: string; name: string; code: string; kind: SectionKind } | undefined
  >();
  const [isSaving, setIsSaving] = useState(false);

  const pythonLangs = useMemo(() => [langs.python()], []);

  // A private key is never handed to the editor; see resolveEditorContent.
  // Memoised on its three inputs so the credential scan of the file text runs
  // when one of them changes, not on every render of the panel.
  const secretHiddenNotice = useSecretHiddenNotice();
  const withholdKeys = useBobcatFlag('fiveStageNav') === 'allowed';
  const fileText = editor?.code;
  const editorContent = useMemo(
    () =>
      resolveEditorContent(fileText, secretHiddenNotice, {
        isViewer,
        withholdKeys,
      }),
    [fileText, secretHiddenNotice, isViewer, withholdKeys]
  );

  const saveEditor = async (code: string) => {
    if (!editor) return;
    const source = sectionOf(editor.kind);
    const { solutionId, versionName } = source;
    if (!solutionId || !versionName) return;

    setIsSaving(true);
    try {
      // Written back over the file it opened, with the sha the list reported,
      // so a concurrent edit surfaces as a conflict (see actions-tab.tsx).
      await syncAgentManagerFiles({
        solutionId,
        versionName,
        commitMessage: `Edited ${editor.path}`,
        changes: buildUploadChanges({
          type: editor.kind,
          files: [
            {
              relativePath: readTypeRelativePath(editor.kind, editor.path),
              file: new File([code], editor.name, { type: 'text/plain' }),
            },
          ],
          existing: source.files,
        }),
      });
      source.refetch();
      setEditor(undefined);
    } catch (error) {
      reportError(error, 'Error saving file');
    } finally {
      setIsSaving(false);
    }
  };

  const { editorModal, openModal: openEditorModal } = useEditorModal({
    loading: isSaving,
    code: editorContent.code,
    title: editor?.name,
    langs: pythonLangs,
    notice: editorContent.notice,
    viewOnly: editorContent.viewOnly,
    onSave: (code: string) => void saveEditor(code),
    onClose: () => {
      setEditor(undefined);
      setIsSaving(false);
    },
  });

  const openEditor = useCallback(
    async ({ name, path, type }: RowSelectionChangeProps) => {
      const kind: SectionKind = type === 'configFile' ? 'config' : 'scripts';
      const { solutionId, versionName } = sectionOf(kind);
      if (!solutionId || !versionName) return;
      // A binary file (a certificate) is never opened as text.
      if (!isEditableActionFile(name)) return;
      try {
        const code = await fetchAgentManagerFileText({
          solutionId,
          versionName,
          path,
        });
        setEditor({ path, name, code, kind });
        openEditorModal();
      } catch (error) {
        reportError(error, `Error opening ${name}`);
      }
    },
    [sectionOf, openEditorModal]
  );

  const upload = async (fileList: FileList | null, kind: SectionKind) => {
    if (!fileList?.length) return;
    const source = sectionOf(kind);
    const { solutionId, versionName } = source;
    if (!solutionId || !versionName) return;

    const picked = Array.from(fileList);
    const conflicts = findUploadConflicts(
      picked.map((file) => file.name),
      source.files.map((file) => file.path || '')
    );
    if (!(await confirmOverwrite(conflicts))) return;

    try {
      await syncAgentManagerFiles({
        solutionId,
        versionName,
        commitMessage: `Uploaded ${picked.length} file(s)`,
        changes: buildUploadChanges({
          type: kind,
          files: picked.map((file) => ({ relativePath: file.name, file })),
          existing: source.files,
        }),
      });
      source.refetch();
    } catch (error) {
      if (error instanceof AgentManagerConflictError) source.refetch();
      reportError(error, 'Error uploading files');
    }
  };

  const onUpload =
    (kind: SectionKind) => (event: ChangeEvent<HTMLInputElement>) => {
      void upload(event.target.files, kind);
      // Allow re-picking the same file after a cancelled overwrite.
      event.target.value = '';
    };

  const remove = useCallback(
    async (file: ActionFile, kind: SectionKind) => {
      const { solutionId, versionName, refetch } = sectionOf(kind);
      if (!solutionId || !versionName) return;
      if (!(await confirmDelete(file.name))) return;
      try {
        const response = await proStudioUrqlGql.deleteAgentManagerFile({
          filePath: file.path,
          solutionId,
          versionName,
        });
        if (response.error) throw new Error(response.error.message);
        refetch();
      } catch (error) {
        reportError(error, `Error deleting ${file.name}`);
      }
    },
    [sectionOf, confirmDelete]
  );

  const download = useCallback(
    async (file: ActionFile, kind: SectionKind) => {
      const { solutionId, versionName } = sectionOf(kind);
      if (!solutionId || !versionName) return;
      try {
        await downloadOneAgentManagerFile({
          solutionId,
          versionName,
          path: file.path,
          fileName: file.name,
        });
      } catch (error) {
        reportError(error, `Error downloading ${file.name}`);
      }
    },
    [sectionOf]
  );

  const columnsFor = useCallback(
    (kind: SectionKind): ColumnDef<ActionFile>[] => [
      {
        accessorKey: 'name',
        // The section heading says what these are.
        header: () => '',
        cell: ({ row }) => {
          const name = row?.original?.name ?? '';
          const note = kind === 'scripts' ? scriptNote?.(name) : undefined;
          const label = html`<ps-text-subtitle size="small">
              ${name}
            </ps-text-subtitle>
            ${note
              ? html`<ps-text-caption variant="tertiary"
                  >${note}</ps-text-caption
                >`
              : ''}`;
          if (
            kind !== 'scripts' ||
            !onScriptDragStart ||
            !name.endsWith('.py') ||
            isViewer
          ) {
            return label;
          }
          // Inline styles: the table draws its cells where the panel's
          // stylesheet can't reach.
          return html`<div
            draggable="true"
            title="Drag onto the canvas to add an action node"
            style="display:flex;align-items:center;gap:6px;cursor:grab"
            @dragstart=${(e: DragEvent) => onScriptDragStart(name, e)}
            @dragend=${() => onScriptDragEnd?.()}
          >
            <ps-icon
              name="drag-indicator"
              size="xsmall"
              style="flex:none;color:#aeb2bd"
            ></ps-icon>
            <div style="display:flex;flex-direction:column;min-width:0">
              ${label}
            </div>
          </div>`;
        },
      },
      {
        id: 'actions',
        header: () =>
          html`<ps-text-body style="opacity: 0">Actions</ps-text-body>`,
        cell: ({ row }) =>
          html`<ps-stack
            alignItems="center"
            justifyContent="end"
            gutter="xsmall"
            nowrap
          >
            ${isEditableActionFile(row.original.name)
              ? html`<ps-icon-button
                  ?disabled=${row.original.loading}
                  name="edit"
                  size="small"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    void openEditor({
                      name: row.original.name,
                      path: row.original.path,
                      type: kind === 'config' ? 'configFile' : 'scriptFile',
                    });
                  }}
                ></ps-icon-button>`
              : null}
            <ps-icon-button
              ?disabled=${row.original.loading}
              name="download"
              size="small"
              @click=${(e: Event) => {
                e.stopPropagation();
                void download(row.original, kind);
              }}
            ></ps-icon-button>
            ${kind === 'scripts' && !isViewer
              ? html`<ps-icon-button
                  ?disabled=${row.original.loading}
                  name="delete"
                  size="small"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    void remove(row.original, kind);
                  }}
                ></ps-icon-button>`
              : null}
          </ps-stack>`,
        meta: {
          align: 'end',
        },
      },
    ],
    [
      isViewer,
      openEditor,
      download,
      remove,
      scriptNote,
      onScriptDragStart,
      onScriptDragEnd,
    ]
  );
  const scriptColumns = useMemo(() => columnsFor('scripts'), [columnsFor]);
  const configColumns = useMemo(() => columnsFor('config'), [columnsFor]);

  const needle = query?.trim().toLowerCase() ?? '';
  const byName = (list: ActionFile[]) =>
    needle
      ? list.filter((f) => (f.name ?? '').toLowerCase().includes(needle))
      : list;
  const functions = byName(toActionFiles(scripts.files));
  // Display only, as on the Files page: the repo's scaffolding is not the
  // designer's config.
  const configurations = byName(
    toActionFiles(
      config.files.filter(
        (file) => !isRepositoryScaffoldingFile(file.path ?? '')
      )
    )
  );

  return (
    <>
      <input
        ref={scriptsInput}
        type="file"
        hidden
        multiple
        accept={ACTION_FILE_ACCEPT}
        onChange={onUpload('scripts')}
      />
      <ActionSection
        title={`Custom · ${functions.length}`}
        label={t('pro-studio/actions:section2.action', {
          defaultValue: 'Upload Scripts',
        })}
        columns={scriptColumns}
        data={functions}
        type="scriptFile"
        accept={ACTION_FILE_ACCEPT}
        compact
        hideUpload
        onRowSelection={(row) => void openEditor(row)}
        viewOnly={isViewer}
      />
      {betweenSections}
      <ActionSection
        title={`Config files · ${configurations.length}`}
        label={t('pro-studio/actions:section1.action', {
          defaultValue: 'Upload Config',
        })}
        columns={configColumns}
        data={configurations}
        type="configFile"
        accept={CONFIG_FILE_ACCEPT}
        compact
        onUpload={onUpload('config')}
        uploadDisabled={!config.versionName}
        onRowSelection={(row) => void openEditor(row)}
        viewOnly={isViewer}
      />
      {fileConflictModal}
      {confirmDeleteModal}
      {editorModal}
    </>
  );
};
